/**
 * Write an element's markup only when it has actually changed (16.105).
 *
 * ## The bug this exists to prevent
 *
 * 16.104 put the fuel price inside the WARP and Fast Travel buttons with a plain
 * `innerHTML` assignment. Both are refreshed by `updateEngineButtons`, which runs
 * **every frame**. So the label `<span>`s were destroyed and recreated ~60 times a
 * second, and a click could never complete: the element that received
 * `pointerdown` no longer existed by `pointerup`, so the browser had nothing to
 * fire `click` on. Both buttons looked perfect and were completely inert -- and
 * one of them is the only way to travel between systems.
 *
 * The lesson is more general than the two buttons: **rewriting the innards of an
 * interactive element on a render loop silently breaks clicking it.** Nothing
 * about the code looks wrong at the call site, which is exactly why it needs a
 * named helper rather than a remembered rule.
 *
 * Kept free of any DOM type so it is testable -- this project has no jsdom, and
 * adding one to cover four lines would be the wrong trade. It needs only
 * something with an `innerHTML` string, which every real element has.
 */

export interface HasInnerHtml {
  innerHTML: string;
}

export type LabelWriter = (el: HasInnerHtml, html: string) => boolean;

/**
 * Returns a writer that assigns `innerHTML` only on a real change, reporting
 * whether it wrote. One writer per concern; the cache is keyed by element, so
 * several elements can share a writer without interfering.
 */
export function createLabelWriter(): LabelWriter {
  const lastWritten = new WeakMap<HasInnerHtml, string>();
  return (el, html) => {
    if (lastWritten.get(el) === html) return false;
    lastWritten.set(el, html);
    el.innerHTML = html;
    return true;
  };
}
